import React, { forwardRef, useEffect, useMemo } from 'react';

// GLOBAL VARIABLES
import { SELECT } from 'defaults.js';

// GLOBAL FUNCTIONS
import { doCallback } from 'functions';

// LOCAL FUNCTIONS
import { flattenOptions } from '../functions';

// MAIN COMPONENT
export const defaultOption = Component => forwardRef(({
  useDefault,
  ...props
}, ref) => {

  // PROPS
  const {
    name,
    value,
    options,
    multiple,
    allowCustom = SELECT.allowCustom,
    onChange,
    disabled,
    form: {
      touched = {}
    } = {}
  } = props;

  // MEMOS
  const flattenedOptions = useMemo(
    () => flattenOptions(options),
    [options]
  )
  const defaultOptionsMemo = useMemo(
    () => {
      if (useDefault === false || useDefault === undefined) return [];
      if (useDefault === true) return [flattenedOptions[0]];
      return String(useDefault).split(',').reduce((arr, val) => {
        const obj = flattenedOptions.find(o => String(o?.value) === val);
        return obj ? [...arr, obj] : allowCustom ? [...arr, {
          label: val,
          value: val
        }] : arr;
      }, []);
    },
    [useDefault, flattenedOptions, allowCustom]
  )

  // EFFECTS
  useEffect(
    () => {
      if (disabled) return;
      if (value || touched[name]) return; // if value already exists or select has been manually cleared, ignore
      if (!defaultOptionsMemo.length) return; // if no default value is set, ignore
      if (defaultOptionsMemo.length === 1 && defaultOptionsMemo[0].value === undefined) return; // if default value is blank, ignore (to avoid infinite loops)
      setTimeout(() => doCallback(onChange, multiple ? defaultOptionsMemo : defaultOptionsMemo[0]), 1); // set default option
      // This is a hacky method of waiting to call setFieldValue after Formik mounts
    },
    [name, value, multiple, onChange, disabled, defaultOptionsMemo, touched]
  )

  // RENDER
  return <Component
    {...props}
    ref={ref}
  />
})
